import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import calendarSVG from '@plone/volto/icons/calendar.svg';
import {
  buildIcs,
  contentToIcsProps,
  slotToIcsProps,
  slugify,
  toAbsoluteUrl,
} from './icsUtils';

const messages = defineMessages({
  downloadIcs: {
    id: 'ics_download',
    defaultMessage: 'Add to calendar (.ics)',
  },
});

/**
 * Download button that generates an .ics file for a schedule item
 * entirely in the browser. `kind` selects the data shape:
 * 'slot' for schedule-block summaries, 'content' for full content objects.
 */
const IcsDownloadButton = ({ item, kind = 'slot', className }) => {
  const intl = useIntl();
  if (!item?.start || !item?.end) return null;

  const onClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const mapper = kind === 'content' ? contentToIcsProps : slotToIcsProps;
    const url = item?.['@id']
      ? toAbsoluteUrl(flattenToAppURL(item['@id']), window.location.origin)
      : undefined;
    const props = mapper(item, {
      host: window.location.hostname,
      url,
    });
    const ics = buildIcs([props]);
    const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = `${slugify(item.title) || 'event'}.ics`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
  };

  const label = intl.formatMessage(messages.downloadIcs);
  return (
    <button
      type="button"
      className={['ics-download', className].filter(Boolean).join(' ')}
      onClick={onClick}
      title={label}
      aria-label={label}
    >
      <Icon name={calendarSVG} size="18px" />
    </button>
  );
};

IcsDownloadButton.propTypes = {
  item: PropTypes.shape({
    '@id': PropTypes.string,
    title: PropTypes.string,
    start: PropTypes.string,
    end: PropTypes.string,
  }).isRequired,
  kind: PropTypes.oneOf(['slot', 'content']),
  className: PropTypes.string,
};

export default IcsDownloadButton;
